import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import AppShell from '@/components/AppShell';
import Icon from '@/components/Icon';
import { DueSoonCard } from '@/components/house';
import { BalanceCard, ExpenseRow } from '@/components/ledger';
import { MediaCard, StatusCard } from '@/components/status';
import { DogsCard } from '@/components/dogs';
import { Card, IconCircle, PageHeader } from '@/components/ui';
import { cachedToday, getToday, getTripTodo, setTripTodoDone } from '@/lib/api';
import type {
  AgendaItem,
  AgendaKind,
  CaptureLogEntry,
  ComingUp as ComingUpData,
  ComingUpBlock,
  TodayBins,
  TodayHousehold,
  TodayResponse,
  TodayTrip,
  TodayTrips,
  TripTodo,
} from '@/lib/api';
import { shortDate, todayISO } from '@/lib/dates';
import { displayName } from '@/lib/people';
import { safeCurrency } from '@/lib/money';
import { useDueSoon } from '@/lib/useHouse';
import { useApp } from '@/App';

// Today — the hub's landing page (docs/hub-plan.md, "Information
// architecture"). One call, GET /api/me/today, feeds it: the active
// household's status, balance and latest expenses, and the viewer's
// own Tripper Keeper trips with the next 14 days of their bookings and
// reservation windows (plan D16), blended with Plans into one list by day;
// trips further out follow under "Later". The Dogs card (comingUp.dogs, the
// same lines as the Dogs page) sits under Coming up when the dogs are away
// or something is next for them. "Due soon" (house, dogs, car: overdue or
// due within 30 days) comes from /api/house/due and shows only when
// something is. The Family group joins here when that phase lands.

const TK_WEB = 'https://trips.waagewu.com';
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function longToday(): string {
  return new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
}

function whenText(t: TodayTrip): string {
  if (t.daysUntil <= 0) {
    const day = 1 - t.daysUntil;
    return t.nights !== null ? `under way · day ${day} of ${t.nights + 1}` : 'today';
  }
  if (t.daysUntil === 1) return 'tomorrow';
  return `in ${t.daysUntil} days`;
}

// "Liz's trip · you're not going" on a trip the viewer sees but isn't
// on; "Liz's trip" on one someone else owns; nothing on the viewer's own.
function whoseTrip(trip: TodayTrip, me: string): string | null {
  const names = trip.travelers?.length ? trip.travelers : null;
  if (trip.going === false) {
    const who = names ? names.join(' and ') : trip.owner ? displayName(trip.owner) : 'Someone else';
    return `${who}'s trip · you're not going`;
  }
  if (trip.owner && trip.owner !== me.toLowerCase()) return `${displayName(trip.owner)}'s trip`;
  return null;
}

function TripRow({ trip, current, me }: { trip: TodayTrip; current: boolean; me: string }) {
  const who = whoseTrip(trip, me);
  const range = trip.dateEnd && trip.dateEnd !== trip.dateStart
    ? `${shortDate(trip.dateStart)} – ${shortDate(trip.dateEnd)}`
    : shortDate(trip.dateStart);
  const nights = trip.nights !== null && trip.nights > 0
    ? ` · ${trip.nights} night${trip.nights === 1 ? '' : 's'}`
    : '';
  return (
    <a
      className={current ? 'row is-current' : 'row'}
      href={`${TK_WEB}/trip/${encodeURIComponent(trip.tripId)}`}
      target="_blank"
      rel="noopener noreferrer"
    >
      <span className="date-badge" aria-hidden="true">
        <span className="date-badge-mon">{MONTHS[Number(trip.dateStart.slice(5, 7)) - 1]}</span>
        <span className="date-badge-day">{Number(trip.dateStart.slice(8, 10))}</span>
      </span>
      <span className="row-body">
        <span className="row-title">{trip.name}</span>
        <span className="row-meta">
          {range}
          {nights} · {whenText(trip)}
          {who ? ` · ${who}` : ''}
        </span>
      </span>
      <Icon name="external" size={16} className="row-chevron" />
    </a>
  );
}

const AGENDA_ICON: Record<AgendaKind, string> = {
  flight: 'flight',
  rail: 'train',
  'check-in': 'hotel',
  'check-out': 'hotel',
  restaurant: 'dining',
  experience: 'star',
  booking: 'calendar',
  pets: 'paw',
  note: 'file',
  remind: 'bell',
  opens: 'calendar',
  'open-now': 'calendar',
};

/** "Tomorrow · Sun, Oct 4". Agenda days are the place's calendar days. */
function agendaDay(iso: string, daysUntil: number): string {
  const d = new Date(`${iso}T12:00:00`).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  const rel = daysUntil <= 0 ? 'Today' : daysUntil === 1 ? 'Tomorrow' : `In ${daysUntil} days`;
  return `${rel} · ${d}`;
}

function agendaMeta(item: AgendaItem, showTrip: boolean, away?: string): string {
  const parts: string[] = [];
  const forDay = item.forDate ? `for ${shortDate(item.forDate)}` : null;
  switch (item.kind) {
    case 'check-in':
      parts.push(item.time ? `Check in from ${item.time}` : 'Check in');
      break;
    case 'check-out':
      parts.push('Check out');
      break;
    case 'opens':
      parts.push(['Booking opens', item.detail].filter(Boolean).join(' '));
      if (forDay) parts.push(forDay);
      break;
    case 'open-now':
      parts.push('Booking is open, not booked yet');
      if (item.detail) parts.push(item.detail);
      if (forDay) parts.push(forDay);
      break;
    default:
      if (item.time) parts.push(item.endTime ? `${item.time} → ${item.endTime}` : item.time);
      if (item.detail) parts.push(item.detail);
  }
  if (item.confirmation) parts.push(`Conf. ${item.confirmation}`);
  if (showTrip && item.tripName) parts.push(item.tripName);
  // A booking on someone else's trip the viewer isn't on.
  if (away) parts.push(away);
  // A plan on a day a trip covers: say so, so a clash is visible.
  if (item.tripId === null && item.duringTrip) parts.push(`during ${item.duringTrip}`);
  return parts.join(' · ');
}

function AgendaRow({ item, showTrip, away }: { item: AgendaItem; showTrip: boolean; away?: string }) {
  const body = (
    <>
      <IconCircle icon={AGENDA_ICON[item.kind]} tone={item.kind === 'open-now' ? 'warn' : 'neutral'} iconSize={18} />
      <span className="row-body">
        <span className="row-title">{item.title}</span>
        <span className="row-meta is-wrap">{agendaMeta(item, showTrip, away)}</span>
      </span>
    </>
  );
  const { href, internal } = hrefOf(item);
  if (internal) {
    return (
      <Link className="row" to={href}>
        {body}
        <Icon name="chevronRight" size={18} className="row-chevron" />
      </Link>
    );
  }
  return (
    <a className="row" href={href} target="_blank" rel="noopener noreferrer">
      {body}
      <Icon name="external" size={16} className="row-chevron" />
    </a>
  );
}

// Days the blended list covers: the Worker's agenda is today plus 14.
// A trip starting later goes under "Later".
const HORIZON_DAYS = 14;

/** A trip as a row in the day list: starting that day, or under way. */
function TripDayRow({ trip, me }: { trip: TodayTrip; me: string }) {
  const who = whoseTrip(trip, me);
  const nights = trip.nights !== null && trip.nights > 0 ? `${trip.nights} night${trip.nights === 1 ? '' : 's'}` : null;
  const until = trip.dateEnd && trip.dateEnd !== trip.dateStart ? `until ${shortDate(trip.dateEnd)}` : null;
  const lead = trip.daysUntil <= 0 ? `Under way · ${whenText(trip).replace('under way · ', '')}` : 'Trip starts';
  return (
    <a
      className={trip.daysUntil <= 0 ? 'row is-current' : 'row'}
      href={`${TK_WEB}/trip/${encodeURIComponent(trip.tripId)}`}
      target="_blank"
      rel="noopener noreferrer"
    >
      <IconCircle icon="flight" tone="accent" iconSize={18} />
      <span className="row-body">
        <span className="row-title">{trip.name}</span>
        <span className="row-meta">{[lead, nights, until, who].filter(Boolean).join(' · ')}</span>
      </span>
      <Icon name="external" size={16} className="row-chevron" />
    </a>
  );
}

interface DayGroup {
  date: string;
  daysUntil: number;
  trips: TodayTrip[];
  items: AgendaItem[];
}

/** One list by day: trips starting (or under way, on today), bookings,
 *  plans and booking windows, in the order they happen. */
function Timeline({ trips, items, me }: { trips: TodayTrip[]; items: AgendaItem[]; me: string }) {
  const today = todayISO();
  // Bookings on a trip the viewer isn't on say whose trip it is.
  const away = new Map(
    trips.filter((t) => t.going === false).map((t) => [t.tripId, (whoseTrip(t, me) ?? '').split(' · ')[0]]),
  );
  const showTrip = new Set(items.filter((i) => i.tripId !== null).map((i) => i.tripId)).size > 1;
  const groups = new Map<string, DayGroup>();
  const group = (date: string, daysUntil: number) => {
    let g = groups.get(date);
    if (!g) {
      g = { date, daysUntil, trips: [], items: [] };
      groups.set(date, g);
    }
    return g;
  };
  for (const t of trips) {
    if (t.daysUntil <= 0) group(today, 0).trips.push(t);
    else group(t.dateStart, t.daysUntil).trips.push(t);
  }
  for (const item of items) group(item.date, item.daysUntil).items.push(item);
  const days = [...groups.values()].sort((a, b) => a.date.localeCompare(b.date));
  return (
    <>
      {days.map((d) => (
        <div key={d.date} role="group" aria-label={agendaDay(d.date, d.daysUntil)}>
          <div className="agenda-day">{agendaDay(d.date, d.daysUntil)}</div>
          {d.trips.map((t) => (
            <TripDayRow key={t.tripId} trip={t} me={me} />
          ))}
          {d.items.map((item) => (
            <AgendaRow key={item.id} item={item} showTrip={showTrip} away={item.tripId ? away.get(item.tripId) : undefined} />
          ))}
        </div>
      ))}
    </>
  );
}


// ─── Coming up, from the Worker's blocks (coming-up-core.ts) ─────────
//
// The Worker decides the blocks (which trip a row sits under, what's
// important enough to show collapsed, "Liz only"); this renders them.

const SOON_SHOWN = 5;
const LATER_SHOWN = 3;
const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

function weekday(iso: string, short = false): string {
  const name = WEEKDAYS[new Date(`${iso}T12:00:00Z`).getUTCDay()];
  return short ? name.slice(0, 3) : name;
}

/** today, tomorrow, Saturday, in 9 days, in 3 weeks, in 4 months. */
function rel(daysUntil: number, iso: string): string {
  if (daysUntil <= 0) return 'today';
  if (daysUntil === 1) return 'tomorrow';
  if (daysUntil < 7) return weekday(iso);
  if (daysUntil < 14) return `in ${daysUntil} days`;
  if (daysUntil < 60) return `in ${Math.round(daysUntil / 7)} weeks`;
  return `in ${Math.round(daysUntil / 30)} months`;
}

function span(start: string, end: string | null): string {
  if (!end || end === start) return shortDate(start);
  return start.slice(0, 7) === end.slice(0, 7) ? `${shortDate(start)} – ${Number(end.slice(8, 10))}` : `${shortDate(start)} – ${shortDate(end)}`;
}

function tripMeta(trip: TodayTrip, away: string | null): string {
  const nights = trip.nights ? `${trip.nights} night${trip.nights === 1 ? '' : 's'}` : null;
  let lead: string[];
  if (trip.daysUntil < 0) lead = [`Day ${1 - trip.daysUntil} of ${(trip.nights ?? 0) + 1}`, trip.dateEnd ? `until ${shortDate(trip.dateEnd)}` : ''];
  else if (trip.daysUntil === 0) lead = ['Starts today', span(trip.dateStart, trip.dateEnd), nights ?? ''];
  else lead = [span(trip.dateStart, trip.dateEnd), nights ?? '', rel(trip.daysUntil, trip.dateStart)];
  return [away, ...lead].filter(Boolean).join(' · ');
}

function when(item: AgendaItem): string {
  const day = item.daysUntil <= 0 ? 'Today' : weekday(item.date, true);
  return item.time ? `${day} ${item.time}` : day;
}

function subText(item: AgendaItem, expanded: boolean): string {
  const parts: (string | null)[] = [];
  switch (item.kind) {
    case 'check-in':
      parts.push(`Check in · ${item.title}`);
      break;
    case 'check-out':
      parts.push(`Check out · ${item.title}`);
      break;
    case 'opens':
      parts.push(`Booking opens · ${item.title}`, item.detail, item.forDate ? `for ${shortDate(item.forDate)}` : null);
      break;
    case 'open-now':
      parts.push(`Open now, not booked · ${item.title}`, item.forDate ? `for ${shortDate(item.forDate)}` : null);
      break;
    default:
      parts.push(item.title, item.endTime ? `→ ${item.endTime}` : null, item.detail);
  }
  if (expanded && item.confirmation) parts.push(`Conf. ${item.confirmation}`);
  return parts.filter(Boolean).join(' · ');
}

/** Where a row opens: its booking page, its day in Tripper Keeper (a
 *  trip's to-do, from a cache without its todoId: the trip), the Inbox
 *  for a note from email, or Plans (always, for a plan's reminder). A
 *  trip to-do with its todoId isn't a link: it opens its notes in place
 *  and checks off (TodoBlock, TodoSubRow). */
function hrefOf(item: AgendaItem): { href: string; internal: boolean } {
  if (item.kind === 'remind' && item.tripId) return { href: `${TK_WEB}/trip/${encodeURIComponent(item.tripId)}`, internal: false };
  if (item.kind === 'remind') return { href: `/plans${item.reservationId ? `#${item.reservationId}` : ''}`, internal: true };
  if (item.url) return { href: item.url, internal: false };
  if (item.tripId) return { href: `${TK_WEB}/trip/${encodeURIComponent(item.tripId)}/days?d=${item.forDate ?? item.date}`, internal: false };
  if (item.kind === 'note') return { href: '/inbox', internal: true };
  return { href: `/plans${item.reservationId ? `#${item.reservationId}` : ''}`, internal: true };
}

function isTripTodo(item: AgendaItem): item is AgendaItem & { tripId: string; todoId: string } {
  return item.kind === 'remind' && !!item.tripId && !!item.todoId;
}

/** A trip to-do's check-off and its notes, read from Tripper Keeper when first opened. */
function useTripTodo(tripId: string, todoId: string) {
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [todo, setTodo] = useState<TripTodo | 'loading' | 'error' | null>(null);
  const toggle = async () => {
    if (busy) return;
    const next = !done;
    setDone(next);
    setBusy(true);
    setError(null);
    try {
      await setTripTodoDone(tripId, todoId, next);
    } catch (err) {
      setDone(!next);
      setError((err as Error)?.message || "Couldn't reach Tripper Keeper");
    } finally {
      setBusy(false);
    }
  };
  const expand = () => {
    setOpen((v) => !v);
    if (todo === null || todo === 'error') {
      setTodo('loading');
      getTripTodo(tripId, todoId)
        .then((r) => setTodo(r.todo))
        .catch(() => setTodo('error'));
    }
  };
  return { done, busy, error, open, todo, toggle, expand };
}

function TodoCheck({ title, done, busy, onToggle, small }: { title: string; done: boolean; busy: boolean; onToggle: () => void; small?: boolean }) {
  return (
    <button
      type="button"
      className={`list-check${done ? ' is-on' : ''}${small ? ' is-small' : ''}`}
      role="checkbox"
      aria-checked={done}
      aria-busy={busy}
      aria-label={done ? `Uncheck ${title}` : `Check off ${title}`}
      onClick={onToggle}
    >
      {done && <Icon name="check" size={small ? 11 : 14} stroke={2.6} />}
    </button>
  );
}

function TodoNotes({ todo, error, done }: { todo: TripTodo | 'loading' | 'error' | null; error: string | null; done: boolean }) {
  return (
    <div className="todo-notes">
      {error && <p className="todo-error">{error}</p>}
      {done && !error && <p className="todo-done">Checked off in Tripper Keeper.</p>}
      {todo === 'loading' && <p className="todo-quiet">Loading notes…</p>}
      {todo === 'error' && <p className="todo-quiet">Couldn't load the notes.</p>}
      {todo && typeof todo === 'object' && (
        <>
          {todo.details ? <p className="todo-text">{todo.details}</p> : <p className="todo-quiet">No notes on this to-do.</p>}
          {todo.url && (
            <a className="todo-link" href={todo.url} target="_blank" rel="noopener noreferrer">
              Open in Tripper Keeper ↗
            </a>
          )}
        </>
      )}
    </div>
  );
}

/** A trip to-do as its own Coming up block: check it off, or open its notes. */
function TodoBlock({ block, item }: { block: ComingUpBlock; item: AgendaItem & { tripId: string; todoId: string } }) {
  const t = useTripTodo(item.tripId, item.todoId);
  const meta = [rel(item.daysUntil, item.date).replace(/^./, (c) => c.toUpperCase()), item.time, item.detail, item.tripName];
  return (
    <div className={t.done ? 'row-group is-done' : 'row-group'}>
      <div className="row">
        <Badge iso={block.date} />
        <button type="button" className="row-body todo-open" aria-expanded={t.open} onClick={t.expand}>
          <span className="row-title">{item.title}</span>
          <span className="row-meta is-wrap">
            {t.done ? 'Done' : meta.filter(Boolean).join(' · ')}
            {block.note && <span className={block.noteTone === 'warn' ? 'block-note is-warn' : 'block-note'}> · {block.note}</span>}
          </span>
        </button>
        <TodoCheck title={item.title} done={t.done} busy={t.busy} onToggle={t.toggle} />
      </div>
      {(t.open || t.error) && <TodoNotes todo={t.open ? t.todo : null} error={t.error} done={t.open && t.done} />}
    </div>
  );
}

/** A trip to-do nested under its trip: a small check, and the line opens its notes. */
function TodoSubRow({ item }: { item: AgendaItem & { tripId: string; todoId: string } }) {
  const t = useTripTodo(item.tripId, item.todoId);
  return (
    <>
      <div className={t.done ? 'sub-row is-done' : 'sub-row'}>
        <TodoCheck title={item.title} done={t.done} busy={t.busy} onToggle={t.toggle} small />
        <button type="button" className="todo-open sub-open" aria-expanded={t.open} onClick={t.expand}>
          <span className="sub-when">{when(item)}</span>
          <span className="sub-text">{subText(item, false)}</span>
        </button>
      </div>
      {(t.open || t.error) && <TodoNotes todo={t.open ? t.todo : null} error={t.error} done={t.open && t.done} />}
    </>
  );
}

function SubRow({ item, expanded }: { item: AgendaItem; expanded: boolean }) {
  if (isTripTodo(item)) return <TodoSubRow item={item} />;
  const { href, internal } = hrefOf(item);
  const body = (
    <>
      <Icon name={AGENDA_ICON[item.kind] ?? 'calendar'} size={14} className="sub-icon" />
      <span className="sub-when">{when(item)}</span>
      <span className="sub-text">{subText(item, expanded)}</span>
    </>
  );
  return internal ? (
    <Link className="sub-row" to={href}>
      {body}
    </Link>
  ) : (
    <a className="sub-row" href={href} target="_blank" rel="noopener noreferrer">
      {body}
    </a>
  );
}

function Badge({ iso, tone }: { iso: string; tone?: 'current' | 'due' }) {
  return (
    <span className={`date-badge${tone === 'due' ? ' is-due-soon' : ''}`} aria-hidden="true">
      <span className="date-badge-mon">{MONTHS[Number(iso.slice(5, 7)) - 1]}</span>
      <span className="date-badge-day">{Number(iso.slice(8, 10))}</span>
    </span>
  );
}

function TripBlock({ block }: { block: ComingUpBlock }) {
  const [open, setOpen] = useState(false);
  const trip = block.trip!;
  const current = trip.daysUntil <= 0;
  // The badge is the trip's start (today while under way); block.date,
  // the earliest row, only decides where the block sorts.
  const head = (
    <>
      <Badge iso={current ? block.date : trip.dateStart} />
      <span className="row-body">
        <span className="row-title">{trip.name}</span>
        <span className="row-meta is-wrap">{tripMeta(trip, block.away)}</span>
      </span>
    </>
  );
  const tk = `${TK_WEB}/trip/${encodeURIComponent(trip.tripId)}`;
  return (
    <div className="row-group">
      {block.hidden.length > 0 ? (
        <button
          type="button"
          className={current ? 'row is-current' : 'row'}
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
          title={open ? 'Show less' : `${block.hidden.length} more`}
        >
          {head}
          <Icon name="chevronDown" size={18} className={open ? 'row-chevron is-open' : 'row-chevron'} />
        </button>
      ) : (
        <a className={current ? 'row is-current' : 'row'} href={tk} target="_blank" rel="noopener noreferrer">
          {head}
          <Icon name="external" size={16} className="row-chevron" />
        </a>
      )}
      {(open ? [...block.shown, ...block.hidden].sort((a, b) => a.date.localeCompare(b.date) || (a.time ?? '').localeCompare(b.time ?? '')) : block.shown).map((item) => (
        <SubRow key={item.id} item={item} expanded={open} />
      ))}
      {open && (
        <a className="sub-row" href={tk} target="_blank" rel="noopener noreferrer">
          <span className="sub-when" />
          <span className="sub-text">Open in Tripper Keeper ↗</span>
        </a>
      )}
    </div>
  );
}

function ItemBlock({ block }: { block: ComingUpBlock }) {
  const item = block.item!;
  if (isTripTodo(item)) return <TodoBlock block={block} item={item} />;
  const { href, internal } = hrefOf(item);
  const meta =
    block.type === 'window'
      ? item.kind === 'open-now'
        ? ['Open now, not booked', item.forDate ? `for ${shortDate(item.forDate)}` : null, item.tripName]
        : ['Booking opens', item.detail, item.forDate ? `for ${shortDate(item.forDate)}` : null, item.tripName]
      : [rel(item.daysUntil, item.date).replace(/^./, (c) => c.toUpperCase()), item.time, item.detail, item.tripName];
  const body = (
    <>
      <Badge iso={block.date} tone={block.type === 'window' ? 'due' : undefined} />
      <span className="row-body">
        <span className="row-title">{item.title}</span>
        <span className="row-meta is-wrap">
          {meta.filter(Boolean).join(' · ')}
          {block.note && <span className={block.noteTone === 'warn' ? 'block-note is-warn' : 'block-note'}> · {block.note}</span>}
        </span>
      </span>
    </>
  );
  return (
    <div className="row-group">
      {internal ? (
        <Link className="row" to={href}>
          {body}
          <Icon name="chevronRight" size={18} className="row-chevron" />
        </Link>
      ) : (
        <a className="row" href={href} target="_blank" rel="noopener noreferrer">
          {body}
          <Icon name="external" size={16} className="row-chevron" />
        </a>
      )}
    </div>
  );
}

/** "From your email · 2 added"; with a member's shared mail in it,
 *  "From email · 2 added (1 from Liz)". Still one line. */
function capturedLine(recent: CaptureLogEntry[], me: string): string | null {
  const counts = capturedCounts(recent);
  if (!counts) return null;
  const byOwner = new Map<string, number>();
  for (const r of recent) {
    const owner = r.owner?.toLowerCase();
    if (owner && owner !== me.toLowerCase()) byOwner.set(owner, (byOwner.get(owner) ?? 0) + 1);
  }
  if (!byOwner.size) return `From your email · ${counts}`;
  const theirs = [...byOwner].map(([owner, n]) => `${n} from ${displayName(owner)}`).join(', ');
  return `From email · ${counts} (${theirs})`;
}

function capturedCounts(recent: CaptureLogEntry[]): string | null {
  const count = (ops: string[]) => recent.filter((r) => ops.includes(r.op)).length;
  const notes = count(['note']);
  const parts = [
    [count(['trip-add', 'plan-add']), 'added'],
    [count(['updated']), 'changed'],
    [count(['cancelled']), 'cancelled'],
    [notes, notes === 1 ? 'note' : 'notes'],
    [count(['review']), 'to review'],
    [count(['suggestion']), 'expense to add'],
    // Mail someone sent in that turned into nothing (a duplicate, unreadable).
    [count(['nothing']), 'with nothing added'],
    // Mail about the apps, sent on to the feedback queue (/feedback).
    [count(['feedback']), 'sent as feedback'],
  ]
    .filter(([n]) => (n as number) > 0)
    .map(([n, w]) => `${n} ${w}`);
  return parts.length ? parts.join(' · ') : null;
}

function ComingUpBlocks({
  data,
  captured,
  me,
}: {
  data: ComingUpData;
  captured: { tellMe: 'email' | 'home'; recent: CaptureLogEntry[] } | null;
  me: string;
}) {
  const [moreSoon, setMoreSoon] = useState(false);
  const [moreLater, setMoreLater] = useState(false);
  const soon = moreSoon ? data.soon : data.soon.slice(0, SOON_SHOWN);
  const later = moreLater ? data.later : data.later.slice(0, LATER_SHOWN);
  const fromEmail = captured?.tellMe === 'home' ? capturedLine(captured.recent, me) : null;
  return (
    <>
      {data.soon.length === 0 && data.later.length === 0 && (
        <p className="card-note">
          No trips or plans coming up. <Link to="/plans">Add a plan</Link>
        </p>
      )}
      {soon.map((b) => (b.type === 'trip' ? <TripBlock key={b.key} block={b} /> : <ItemBlock key={b.key} block={b} />))}
      {data.soon.length > SOON_SHOWN && !moreSoon && (
        <button type="button" className="show-more" onClick={() => setMoreSoon(true)}>
          Show {data.soon.length - SOON_SHOWN} more
        </button>
      )}
      {later.length > 0 && (
        <div role="group" aria-label="Later">
          {data.soon.length > 0 && <div className="agenda-day">Later</div>}
          {later.map(({ trip, away }) => (
            <div className="row-group" key={trip.tripId}>
              <a className="row" href={`${TK_WEB}/trip/${encodeURIComponent(trip.tripId)}`} target="_blank" rel="noopener noreferrer">
                <Badge iso={trip.dateStart} />
                <span className="row-body">
                  <span className="row-title">{trip.name}</span>
                  <span className="row-meta is-wrap">{tripMeta(trip, away)}</span>
                </span>
                <Icon name="external" size={16} className="row-chevron" />
              </a>
            </div>
          ))}
          {data.later.length > LATER_SHOWN && !moreLater && (
            <button type="button" className="show-more" onClick={() => setMoreLater(true)}>
              Show {data.later.length - LATER_SHOWN} more trip{data.later.length - LATER_SHOWN === 1 ? '' : 's'}
            </button>
          )}
        </div>
      )}
      {fromEmail && (
        <Link className="card-foot" to="/inbox">
          {fromEmail} ›
        </Link>
      )}
    </>
  );
}

function ComingUp({
  trips,
  agenda: whole,
  me,
  blocks,
  captured,
}: {
  trips: TodayTrips | null;
  agenda?: AgendaItem[];
  me: string;
  blocks?: ComingUpData;
  captured?: { tellMe: 'email' | 'home'; recent: CaptureLogEntry[] };
}) {
  const all = trips ? [...trips.current, ...trips.upcoming] : [];
  // The whole agenda (trips and Plans) when the Worker sends it.
  const agenda = whole ?? trips?.agenda ?? [];
  const plans = agenda.filter((i) => i.tripId === null);
  const soon = all.filter((t) => t.daysUntil <= HORIZON_DAYS);
  const later = all.filter((t) => t.daysUntil > HORIZON_DAYS);
  const nothing = soon.length === 0 && agenda.length === 0;
  return (
    <Card>
      <div className="card-head">
        <h2 className="card-title">Coming up</h2>
        {trips?.linked && (
          <a href={TK_WEB} target="_blank" rel="noopener noreferrer">
            Tripper Keeper
          </a>
        )}
      </div>
      {trips === null ? (
        <p className="loading">Loading…</p>
      ) : !trips.linked ? (
        <>
          {plans.length > 0 && <Timeline trips={[]} items={plans} me={me} />}
          <Link to="/trips" className="row">
            <IconCircle icon="flight" tone="accent" />
            <span className="row-body">
              <span className="row-title">Connect Tripper Keeper</span>
              <span className="row-meta">Your trips show up here once your account is linked.</span>
            </span>
            <Icon name="chevronRight" size={18} className="row-chevron" />
          </Link>
        </>
      ) : (
        blocks ? (
          <ComingUpBlocks data={blocks} captured={captured ?? null} me={me} />
        ) : (
        <>
          {nothing && (
            <p className="card-note">{trips.error ? "Tripper Keeper didn't answer." : 'Nothing in the next two weeks.'}</p>
          )}
          {!nothing && <Timeline trips={soon} items={agenda} me={me} />}
          {later.length > 0 && (
            <div role="group" aria-label="Later">
              <div className="agenda-day">Later</div>
              {later.map((t) => (
                <TripRow key={t.tripId} trip={t} current={false} me={me} />
              ))}
            </div>
          )}
        </>
        )
      )}
      {trips?.linked && trips.stale && all.length > 0 && (
        <p className="card-note">
          Tripper Keeper didn't answer, so these are the trips from{' '}
          {trips.fetchedAt ? new Date(trips.fetchedAt).toLocaleString() : 'earlier'}.
        </p>
      )}
    </Card>
  );
}

// The house's next bin pickup, worded on the Worker (bins-core.ts):
// "Garbage + recycling out tonight", "Recycling only Friday".
function BinsCard({ bins }: { bins: TodayBins | null | undefined }) {
  if (!bins) return null;
  const now = bins.when !== 'later';
  return (
    <Card>
      <div className={now ? 'row is-current' : 'row'}>
        <IconCircle icon="trash" tone={now ? 'accent' : 'neutral'} iconSize={18} />
        <span className="row-body">
          <span className="row-title">{bins.title}</span>
          <span className="row-meta is-wrap">{bins.meta}</span>
        </span>
      </div>
    </Card>
  );
}

function RecentCard({ household, me }: { household: TodayHousehold | null; me: string }) {
  const baseCurrency = safeCurrency(household?.balance.baseCurrency, household?.currency);
  const memberEmails = household?.members.map((m) => m.email) ?? [];
  return (
    <Card>
      <div className="card-head">
        <h2 className="card-title">Recent in the ledger</h2>
        <Link to="/ledger">Open ledger</Link>
      </div>
      {household === null ? (
        <p className="loading">Loading…</p>
      ) : household.recent.length === 0 ? (
        <p className="card-note">Nothing added yet.</p>
      ) : (
        household.recent.map((e) => (
          <ExpenseRow key={e.id} expense={e} baseCurrency={baseCurrency} memberEmails={memberEmails} me={me} />
        ))
      )}
    </Card>
  );
}

export default function Today() {
  const { me, household } = useApp();
  const [data, setData] = useState<TodayResponse | null>(() => cachedToday());
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const dueSoon = useDueSoon();
  const reloadDue = dueSoon.reload;

  const load = useCallback((refresh: boolean) => {
    setRefreshing(true);
    if (refresh) reloadDue();
    return getToday(todayISO(), { refresh })
      .then((r) => {
        setData(r);
        setError(null);
      })
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load Today.'))
      .finally(() => setRefreshing(false));
  }, [reloadDue]);

  useEffect(() => {
    load(false);
  }, [load]);

  const mine = data?.households.find((h) => h.householdId === household.householdId) ?? null;
  const baseCurrency = safeCurrency(mine?.balance.baseCurrency, household.currency);

  return (
    <AppShell wide>
      <PageHeader
        title="Today"
        subtitle={longToday()}
        actions={
          <button
            type="button"
            className="icon-btn"
            onClick={() => load(true)}
            disabled={refreshing}
            aria-label="Refresh"
            title="Refresh"
          >
            <Icon name="refresh" size={18} />
          </button>
        }
      />
      <div className={dueSoon.items.length > 0 ? 'today-grid has-due' : 'today-grid'}>
        {mine?.status && mine.status.level !== 'none' && (
          <div className="today-status">
            <StatusCard view={mine.status} householdId={mine.householdId} onChange={() => void load(false)} />
          </div>
        )}
        <div className="today-balance">
          <BalanceCard
            pair={mine?.balance.pairwise[0] ?? null}
            me={me.email}
            baseCurrency={baseCurrency}
            isOwner={household.role === 'owner'}
            loading={mine === null && error === null}
            error={mine === null ? error : null}
          />
          {/* What's downloading and what was added (alexandria's collector). */}
          <MediaCard view={mine?.status} />
        </div>
        <div className="today-coming">
          <BinsCard bins={data?.bins} />
          <ComingUp
            trips={data?.trips ?? null}
            agenda={data?.agenda}
            me={me.email}
            blocks={data?.comingUp}
            captured={data?.captured}
          />
          {/* The dogs: who has them today and what's next (the stay's
              drop-off and return are in Coming up too). */}
          <DogsCard dogs={data?.comingUp?.dogs} />
        </div>
        {dueSoon.items.length > 0 && (
          <div className="today-due">
            <DueSoonCard items={dueSoon.items} />
          </div>
        )}
        <div className="today-recent">
          <RecentCard household={mine} me={me.email} />
        </div>
      </div>
    </AppShell>
  );
}
